// Org Filter Manager entry point. SPEC section 8.
import "@esri/calcite-components/dist/calcite/calcite.css";
import "./app.css";
import { defineCustomElements } from "@esri/calcite-components/dist/loader";

import { signIn, requireAdmin, NotAnAdministratorError } from "./auth";
import { loadAppConfig } from "./api/config";
import { createContext } from "./context";
import { route } from "./router";
import { mountShell } from "./ui/shell";
import { html, errorText } from "./ui/dom";

/**
 * Calcite fetches icons and translations at runtime. They are copied next to
 * index.html by `npm run copy-calcite-assets` so the Manager works on a Portal
 * machine with no route to the Esri CDN.
 */
const appBase = location.origin + location.pathname.replace(/[^/]*$/, "");
defineCustomElements(window, { resourcesUrl: `${appBase}calcite/` });

async function start(): Promise<void> {
  const cfg = await loadAppConfig();          // public/config.json
  const session = await signIn(cfg);          // OAuth 2.0 PKCE, sessionStorage only
  const me = await requireAdmin(session);     // role must be org_admin (SPEC 8.2)
  const ctx = createContext(cfg, session, me);
  // Dev server only: lets the browser console drive the same API layer the pages
  // use, which is how the SPEC's RESEARCH items get checked against a real server.
  // `import.meta.env.DEV` is false in a build, so this never ships.
  if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).orgfilter = ctx;
  const content = mountShell(document.getElementById("app")!, ctx);
  route(content, ctx);                        // hash router over pages/*
}

start().catch((e) => {
  const app = document.getElementById("app")!;
  const denied = e instanceof NotAnAdministratorError;
  app.innerHTML = html`
    <calcite-panel heading="${denied ? "Administrator role required" : "Org Filter Manager could not start"}">
      <calcite-notice open kind="danger" width="full" style="margin: 1rem">
        <div slot="title">${denied ? "You are signed in, but not as an administrator" : "Startup failed"}</div>
        <div slot="message">
          ${denied
            ? `The Org Filter Manager changes who can see which data, so it is only available to Portal administrators. ${(e as NotAnAdministratorError).username} has the role "${(e as NotAnAdministratorError).role}".`
            : errorText(e)}
        </div>
      </calcite-notice>
    </calcite-panel>
  `;
});
